import React, { useEffect, useState, useMemo } from 'react';
import { api, fmt, fmtDate } from '../utils/api';
import Topbar from '../components/Topbar';
import { Badge, StatusBadge, EmptyState, Spinner, SearchInput, useToast, Modal } from '../components/UI';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function parseMonthDate(m) {
  if (!m) return 0;
  const p = String(m).split(' ');
  return parseInt(p[1]||0)*12 + MONTH_NAMES.indexOf(p[0]);
}

export default function Declarations({ user, directorView }) {
  const [declarations, setDeclarations] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [showMissing, setShowMissing] = useState(false);
  const [showToast, Toast] = useToast();
  const [editModal, setEditModal] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [newModal, setNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ franchiseId:'', month:'', income:'', rent:'', salaries:'', royalty:'', otherExpenses:'', paymentType:'submit_and_pay' });
  const [payModal, setPayModal] = useState(null); // { d, resolve }
  const [detailModal, setDetailModal] = useState(null);
  const [collapsedYears, setCollapsedYears] = useState({});
  // All years start expanded — user can collapse them
  const isAdmin = user?.role === 'admin' || user?.role === 'hq_support';

  const load = () => {
    Promise.all([api.getDeclarations(directorView), api.getFranchises(directorView)]).then(([d, f]) => {
      // Enrich declarations with branch location
      const franchiseMap = {};
      (f||[]).forEach(fr => { franchiseMap[fr.id] = fr; });
      const enriched = (d||[]).map(decl => ({
        ...decl,
        branchLocation: franchiseMap[decl.franchiseId]?.location || decl.branchLocation || null,
        franchiseName: decl.franchiseName || franchiseMap[decl.franchiseId]?.name || '',
      }));
      setDeclarations(enriched);
      setFranchises(f || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  // Missing declarations — same logic as dashboard
  const missingDecls = useMemo(() => {
    const now = new Date();
    const GLOBAL_START_YR = 2025, GLOBAL_START_MO = 8;
    const missing = [];
    franchises.filter(f => f.status !== 'closed').forEach(f => {
      let startYr = GLOBAL_START_YR, startMo = GLOBAL_START_MO;
      if (f.royaltyStartMonth) {
        const parts = f.royaltyStartMonth.split(' ');
        const mo = MONTH_NAMES.indexOf(parts[0]);
        const yr = parseInt(parts[1]);
        if (mo >= 0 && yr >= 2024) { startYr = yr; startMo = mo; }
      }
      const startIdx = startYr * 12 + startMo;
      const nowIdx = now.getFullYear() * 12 + now.getMonth();
      for (let idx = startIdx; idx < nowIdx; idx++) {
        const y = Math.floor(idx / 12);
        const m = idx % 12;
        const due = new Date(y, m + 1, 10);
        if (due > now) break;
        const mStr = MONTH_NAMES[m] + ' ' + y;
        const hasDecl = declarations.some(d => d.franchiseId === f.id && d.month === mStr);
        const hasFinancials = (f.financials||[]).some(fin => fin.month === mStr);
        if (!hasDecl && !hasFinancials) missing.push({ franchise: f, month: mStr });
      }
    });
    return missing.sort((a, b) => parseMonthDate(b.month) - parseMonthDate(a.month));
  }, [franchises, declarations]);

  const filteredMissing = missingDecls.filter(m => {
    const q = search.toLowerCase();
    return !q || (m.franchise?.name||'').toLowerCase().includes(q) ||
      (m.franchise?.location||'').toLowerCase().includes(q) ||
      (m.month||'').toLowerCase().includes(q);
  });

  const allMonths = [...new Set(declarations.map(d => d.month).filter(Boolean))].sort((a,b) => parseMonthDate(b)-parseMonthDate(a));

  const counts = {
    all: declarations.length,
    pending: declarations.filter(d => d.status === 'pending' && !d.editRequested).length,
    approved: declarations.filter(d => d.status === 'approved').length,
    rejected: declarations.filter(d => d.status === 'rejected').length,
    edit: declarations.filter(d => d.editRequested).length,
  };

  const filtered = declarations.filter(d => {
    const q = search.toLowerCase();
    const matchSearch = !q ||
      (d.franchiseName||'').toLowerCase().includes(q) ||
      (d.branchLocation||'').toLowerCase().includes(q) ||
      (d.month||'').toLowerCase().includes(q);
    const matchStatus = filterStatus === 'all' || d.status === filterStatus || (filterStatus === 'edit' && d.editRequested);
    const matchPayment = filterPayment === 'all' ||
      (filterPayment === 'save_only' && d.paymentType !== 'submit_and_pay') ||
      (filterPayment === 'confirmed' && d.paymentStatus === 'confirmed') ||
      (filterPayment === 'pending_payment' && d.paymentType === 'submit_and_pay' && d.paymentStatus !== 'confirmed');
    const matchBranch = filterBranch === 'all' || d.franchiseId === filterBranch;
    const matchMonth = filterMonth === 'all' || d.month === filterMonth;
    return matchSearch && matchStatus && matchPayment && matchBranch && matchMonth;
  });

  // Group by section then year
  const editRequests = filtered.filter(d => d.editRequested);
  const pending = filtered.filter(d => d.status === 'pending' && !d.editRequested);
  const approved = filtered.filter(d => d.status === 'approved' && !d.editRequested);
  const rejected = filtered.filter(d => d.status === 'rejected' && !d.editRequested);

  function groupByYear(decls) {
    const years = {};
    decls.forEach(d => {
      const yr = (d.month||'').split(' ')[1] || 'Unknown';
      if (!years[yr]) years[yr] = [];
      years[yr].push(d);
    });
    return Object.entries(years).sort((a,b) => parseInt(b[0])-parseInt(a[0]));
  }

  async function approveDecl(d) {
    await api.approveDeclaration(d.id);
    if (d.paymentType === 'submit_and_pay' && parseFloat(d.royalty) > 0) {
      await api.createRoyalty({ franchiseId:d.franchiseId, franchiseName:d.franchiseName, month:d.month, revenue:d.income, royalty:d.royalty, paymentDate:new Date().toISOString().split('T')[0], source:'jotform', paymentMethod:'jotform' });
      api.createXeroInvoiceForRoyalty({ franchiseId:d.franchiseId, franchiseName:d.franchiseName, month:d.month, royalty:d.royalty }).catch(()=>{});
    }
    await api.logActivity('Approved declaration', `${d.franchiseName} — ${d.month}`);
    showToast(d.paymentType==='submit_and_pay'?'Approved & royalty recorded':'Approved'); load();
  }

  async function saveEdit() {
    await api.updateDeclaration(editModal.id, editForm);
    await api.logActivity('Edited declaration', `${editModal.franchiseName} — ${editModal.month}`);
    showToast('Updated'); setEditModal(null); load();
  }

  async function doReject() {
    if (!rejectReason.trim()) { showToast('Please provide a reason'); return; }
    await api.rejectDeclaration(rejectModal.id, rejectReason);
    await api.logActivity('Rejected declaration', `${rejectModal.franchiseName} — ${rejectModal.month} — ${rejectReason}`);
    showToast('Rejected'); setRejectModal(null); setRejectReason(''); load();
  }

  async function deleteDecl(d) {
    if (!window.confirm(`Delete declaration for ${d.franchiseName} (${d.month})?`)) return;
    await api.deleteDeclaration(d.id);
    await api.logActivity('Deleted declaration', `${d.franchiseName} — ${d.month}`);
    showToast('Deleted'); load();
  }

  async function createDeclaration() {
    if (!newForm.franchiseId || !newForm.month) { showToast('Please select a branch and month'); return; }
    const franchise = franchises.find(f => f.id === newForm.franchiseId);
    await api.submitDeclaration({ ...newForm, franchiseName:franchise?.name });
    await api.logActivity('Created declaration', `${franchise?.name} — ${newForm.month}`);
    showToast('Created'); setNewModal(false); load();
  }

  function toggleYear(section, year) {
    const key = `${section}_${year}`;
    setCollapsedYears(c => ({ ...c, [key]: !c[key] }));
  }

  if (loading) return <div style={{ marginLeft:'var(--sidebar-width)' }}><Topbar title="Declarations" /><Spinner /></div>;

  return (
    <div style={{ marginLeft:'var(--sidebar-width)', display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Topbar title="Declarations" subtitle={`${declarations.length} total`} actions={
        isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setNewModal(true)}>
            <i className="ti ti-plus" style={{ fontSize:14 }} /> New Declaration
          </button>
        )
      } />
      {Toast}

      <div style={{ padding:24 }}>
        {/* Filters */}
        <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ flex:1, maxWidth:280 }}>
            <SearchInput value={search} onChange={setSearch} placeholder="Search by branch, name or month..." />
          </div>
          <button onClick={() => setShowMissing(m => !m)} className="btn btn-sm" style={{
            background: showMissing ? 'var(--red)' : '#fff',
            color: showMissing ? '#fff' : 'var(--red)',
            border: '1px solid rgba(163,45,45,0.3)',
            fontWeight: 600,
          }}>
            ⚠ Missing ({missingDecls.length})
          </button>
          <select value={filterStatus} onChange={e=>setFilterStatus(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All Status</option>
            <option value="pending">Pending ({counts.pending})</option>
            <option value="edit">Edit Requests ({counts.edit})</option>
            <option value="approved">Approved ({counts.approved})</option>
            <option value="rejected">Rejected ({counts.rejected})</option>
          </select>
          <select value={filterPayment} onChange={e=>setFilterPayment(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All Payment Types</option>
            <option value="save_only">Save Only</option>
            <option value="confirmed">✓ Payment Confirmed</option>
            <option value="pending_payment">⏳ Payment Pending</option>
          </select>
          <select value={filterBranch} onChange={e=>setFilterBranch(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All Branches</option>
            {franchises.map(f => <option key={f.id} value={f.id}>{f.location||f.name}{f.location&&f.location!==f.name?' ('+f.name+')':''}</option>)}
          </select>
          <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={{ width:'auto', marginBottom:0 }}>
            <option value="all">All Months</option>
            {allMonths.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>

        {/* Missing declarations panel */}
        {showMissing && (
          <div className="card" style={{ marginBottom:20, border:'1px solid rgba(163,45,45,0.2)' }}>
            <div style={{ padding:'12px 16px', background:'rgba(163,45,45,0.04)', borderBottom:'1px solid rgba(163,45,45,0.1)', display:'flex', justifyContent:'space-between', alignItems:'center', borderRadius:'var(--r-lg) var(--r-lg) 0 0', overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <i className="ti ti-alert-triangle" style={{ fontSize:16, color:'var(--red)' }} />
                <span style={{ fontWeight:700, fontSize:13, color:'var(--red)' }}>Missing Declarations — {filteredMissing.length} total</span>
              </div>
              <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>Sorted most recent first</span>
            </div>
            {filteredMissing.length === 0 ? (
              <div style={{ padding:32, textAlign:'center', color:'var(--green)', fontSize:13, fontWeight:500 }}>✓ All declarations submitted</div>
            ) : (
              <table className="table">
                <thead><tr><th>BRANCH</th><th>LOCATION</th><th>MONTH</th><th>STATUS</th></tr></thead>
                <tbody>
                  {filteredMissing.map((m, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight:500, fontSize:13 }}>{m.franchise.name}</td>
                      <td style={{ fontSize:12, color:'var(--text-secondary)' }}>{m.franchise.location||'—'}</td>
                      <td style={{ fontWeight:600, color:'var(--navy)' }}>{m.month}</td>
                      <td><Badge type="red">Missing</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Edit Requests section */}
        {editRequests.length > 0 && (
          <DeclSection title="Edit Requests" color="var(--blue)" decls={editRequests} isAdmin={isAdmin}
            onApprove={approveDecl} onReject={d=>{setRejectModal(d);setRejectReason('');}} onEdit={d=>{setEditModal(d);setEditForm({income:d.income,rent:d.rent,salaries:d.salaries,royalty:d.royalty,otherExpenses:d.otherExpenses});}} onDelete={deleteDecl}
            onDetail={setDetailModal} onConfirmPayment={d => setPayModal(d)}
            onApproveEdit={async d=>{await api.approveEditRequest(d.id);await api.logActivity('Approved edit request',`${d.franchiseName} — ${d.month}`);showToast('Edit approved');load();}}
            onRejectEdit={d=>{setRejectModal({...d,isEdit:true});setRejectReason('');}}
            groupByYear={groupByYear} collapsedYears={collapsedYears} toggleYear={toggleYear} sectionKey="edit" />
        )}

        {/* Pending section */}
        {pending.length > 0 && (
          <DeclSection title="Pending" color="var(--amber)" decls={pending} isAdmin={isAdmin}
            onApprove={approveDecl} onReject={d=>{setRejectModal(d);setRejectReason('');}} onEdit={d=>{setEditModal(d);setEditForm({income:d.income,rent:d.rent,salaries:d.salaries,royalty:d.royalty,otherExpenses:d.otherExpenses});}} onDelete={deleteDecl}
            onDetail={setDetailModal} onConfirmPayment={d => setPayModal(d)}
            groupByYear={groupByYear} collapsedYears={collapsedYears} toggleYear={toggleYear} sectionKey="pending" />
        )}

        {/* Approved section */}
        {approved.length > 0 && (
          <DeclSection title="Approved" color="var(--green)" decls={approved} isAdmin={isAdmin}
            onEdit={d=>{setEditModal(d);setEditForm({income:d.income,rent:d.rent,salaries:d.salaries,royalty:d.royalty,otherExpenses:d.otherExpenses});}} onDelete={deleteDecl}
            onDetail={setDetailModal} onConfirmPayment={d => setPayModal(d)}
            onSettleAmendment={async d=>{await api.markAmendmentSettled(d.id);showToast('Amendment settled');load();}}
            groupByYear={groupByYear} collapsedYears={collapsedYears} toggleYear={toggleYear} sectionKey="approved" />
        )}

        {/* Rejected section */}
        {rejected.length > 0 && (
          <DeclSection title="Rejected" color="var(--red)" decls={rejected} isAdmin={isAdmin}
            onEdit={d=>{setEditModal(d);setEditForm({income:d.income,rent:d.rent,salaries:d.salaries,royalty:d.royalty,otherExpenses:d.otherExpenses});}} onDelete={deleteDecl}
            onDetail={setDetailModal}
            groupByYear={groupByYear} collapsedYears={collapsedYears} toggleYear={toggleYear} sectionKey="rejected" />
        )}

        {filtered.length === 0 && (
          <div className="card" style={{ padding:48 }}><EmptyState icon="ti-file-text" message="No declarations found" /></div>
        )}
      </div>

      {/* Confirm Payment Modal */}
      {payModal && (
        <ConfirmPayModal
          d={payModal}
          onConfirm={async (paymentMethod, datePaid) => {
            await api.confirmPayment(payModal.id, paymentMethod, datePaid);
            showToast('Payment confirmed');
            setPayModal(null);
            load();
          }}
          onCancel={() => setPayModal(null)}
        />
      )}
      {/* Detail Modal */}
      {detailModal && (
        <Modal title={`${detailModal.branchLocation ? detailModal.branchLocation+(detailModal.branchLocation!==detailModal.franchiseName?' ('+detailModal.franchiseName+')':'') : detailModal.franchiseName} — ${detailModal.month}`} onClose={() => setDetailModal(null)} width={560}
          footer={<button className="btn btn-secondary" onClick={() => setDetailModal(null)}>Close</button>}>
          <DeclDetail d={detailModal} />
        </Modal>
      )}

      {/* Edit Modal */}
      {editModal && (
        <Modal title={`Edit — ${editModal.branchLocation||editModal.franchiseName} ${editModal.month}`} onClose={() => setEditModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setEditModal(null)}>Cancel</button><button className="btn btn-primary" onClick={saveEdit}>Save</button></>}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            {[['INCOME','income'],['RENT','rent'],['SALARIES','salaries'],['ROYALTY','royalty'],['OTHER EXPENSES','otherExpenses']].map(([l,k]) => (
              <div key={k}><label>{l} (£)</label><input type="number" value={editForm[k]||''} onChange={e=>setEditForm(f=>({...f,[k]:e.target.value}))} /></div>
            ))}
          </div>
          <div style={{ marginTop:12, padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:12 }}>
            Net: <strong style={{ color:((parseFloat(editForm.income)||0)-((parseFloat(editForm.rent)||0)+(parseFloat(editForm.salaries)||0)+(parseFloat(editForm.royalty)||0)+(parseFloat(editForm.otherExpenses)||0)))>=0?'var(--green)':'var(--red)' }}>
              {fmt((parseFloat(editForm.income)||0)-((parseFloat(editForm.rent)||0)+(parseFloat(editForm.salaries)||0)+(parseFloat(editForm.royalty)||0)+(parseFloat(editForm.otherExpenses)||0)))}
            </strong>
          </div>
        </Modal>
      )}

      {/* Reject Modal */}
      {rejectModal && (
        <Modal title={rejectModal.isEdit?'Reject Edit Request':'Reject Declaration'} onClose={() => setRejectModal(null)}
          footer={<><button className="btn btn-secondary" onClick={() => setRejectModal(null)}>Cancel</button><button className="btn btn-danger" onClick={async () => {
            if (!rejectReason.trim()) { showToast('Please provide a reason'); return; }
            if (rejectModal.isEdit) {
              await api.rejectEditRequest(rejectModal.id, rejectReason);
              await api.logActivity('Rejected edit request', `${rejectModal.franchiseName} — ${rejectModal.month}`);
            } else {
              await api.rejectDeclaration(rejectModal.id, rejectReason);
              await api.logActivity('Rejected declaration', `${rejectModal.franchiseName} — ${rejectModal.month}`);
            }
            showToast('Rejected'); setRejectModal(null); setRejectReason(''); load();
          }}>Reject</button></>}>
          <div style={{ marginBottom:8, fontSize:13, color:'var(--text-secondary)' }}>{rejectModal.franchiseName} · {rejectModal.month}</div>
          <label>REASON FOR REJECTION</label>
          <textarea value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} placeholder="Provide a reason..." style={{ resize:'none' }} />
        </Modal>
      )}

      {/* New Declaration Modal */}
      {newModal && (
        <Modal title="New Declaration" onClose={() => setNewModal(false)}
          footer={<><button className="btn btn-secondary" onClick={() => setNewModal(false)}>Cancel</button><button className="btn btn-primary" onClick={createDeclaration}>Create</button></>}>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div><label>BRANCH *</label>
              <select value={newForm.franchiseId} onChange={e=>setNewForm(f=>({...f,franchiseId:e.target.value,month:''}))}>
                <option value="">Select branch...</option>
                {franchises.map(f=><option key={f.id} value={f.id}>{f.location||f.name}{f.location&&f.location!==f.name?' — '+f.name:''}</option>)}
              </select>
            </div>
            <div><label>MONTH *</label>
              <select value={newForm.month} onChange={e=>setNewForm(f=>({...f,month:e.target.value}))}>
                <option value="">Select month...</option>
                {(() => {
                  const MTHS=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                  const now = new Date();
                  const selectedFranchise = franchises.find(f=>f.id===newForm.franchiseId);
                  const startMonth = selectedFranchise?.royaltyStartMonth || 'Sep 2025';
                  const startParts = startMonth.split(' ');
                  const startIdx = parseInt(startParts[1])*12+MTHS.indexOf(startParts[0]);
                  const endIdx = now.getFullYear()*12+now.getMonth();
                  const submittedMonths = new Set(declarations.filter(d=>d.franchiseId===newForm.franchiseId).map(d=>d.month));
                  const options = [];
                  for (let i=startIdx; i<=endIdx; i++) {
                    const yr=Math.floor(i/12), mo=i%12;
                    const mStr=MTHS[mo]+' '+yr;
                    if (!submittedMonths.has(mStr)) options.push(mStr);
                  }
                  if (!newForm.franchiseId) {
                    // No branch selected — show all months
                    const allOpts = [];
                    const globalStart = 2025*12+8;
                    for (let i=globalStart; i<=endIdx; i++) { const yr=Math.floor(i/12),mo=i%12; allOpts.push(MTHS[mo]+' '+yr); }
                    return allOpts.reverse().map(m=><option key={m} value={m}>{m}</option>);
                  }
                  if (options.length === 0) return <option value="">All months submitted ✓</option>;
                  return options.reverse().map(m=><option key={m} value={m}>{m}</option>);
                })()}
              </select>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              {[['INCOME','income'],['RENT','rent'],['SALARIES','salaries'],['OTHER','otherExpenses']].map(([l,k]) => (
                <div key={k}><label>{l} (£)</label><input type="number" value={newForm[k]||''} onChange={e=>{
                  const val = e.target.value;
                  if (k === 'income') {
                    const royalty = val ? (parseFloat(val) * 0.125).toFixed(2) : '';
                    setNewForm(f=>({...f, income:val, royalty }));
                  } else {
                    setNewForm(f=>({...f,[k]:val}));
                  }
                }} /></div>
              ))}
              <div><label>ROYALTY (£) <span style={{ fontSize:10, color:'var(--text-tertiary)' }}>12.5% auto</span></label>
                <input type="number" value={newForm.royalty||''} readOnly style={{ background:'var(--bg)', color:'var(--text-secondary)', cursor:'not-allowed' }} />
              </div>
            </div>
            <div><label>PAYMENT TYPE</label>
              <select value={newForm.paymentType} onChange={e=>setNewForm(f=>({...f,paymentType:e.target.value}))}>
                <option value="save_only">Save Only</option>
                <option value="submit_and_pay">Submit & Pay</option>
              </select>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Declaration Detail component ─────────────────────────────────────────────
function DeclDetail({ d }) {
  const exp = (parseFloat(d.rent)||0)+(parseFloat(d.salaries)||0)+(parseFloat(d.royalty)||0)+(parseFloat(d.otherExpenses)||0);
  const net = (parseFloat(d.income)||0) - exp;
  const margin = d.income > 0 ? (net/parseFloat(d.income)*100).toFixed(1) : 0;
  const hasAmend = d.amendedIncome !== null && d.amendedIncome !== undefined;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {/* Header */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
        <StatusBadge status={d.editRequested?'edit_requested':d.status} />
        {d.paymentType === 'submit_and_pay' && <Badge type={d.paymentStatus==='confirmed'?'green':'amber'}>{d.paymentStatus==='confirmed'?'✓ Paid':'Payment Pending'}</Badge>}
        {d.paymentType !== 'submit_and_pay' && d.paymentStatus === 'confirmed' && <Badge type="green">✓ Paid</Badge>}
        {hasAmend && <Badge type={d.amendmentSettled?'green':'amber'}>{d.amendmentSettled?'Amendment Settled':'Amendment Pending'}</Badge>}
      </div>
      <div style={{ display:'flex', gap:12, fontSize:12, color:'var(--text-secondary)' }}>
        <span><strong>Submitted by:</strong> {d.franchiseeName||'—'}</span>
        <span><strong>Submitted:</strong> {d.submittedAt ? new Date(d.submittedAt).toLocaleDateString('en-GB') : '—'}</span>
      </div>

      {/* Figures grid */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
        {[
          { label:'INCOME', value:d.income, color:'var(--navy)' },
          { label:'RENT', value:d.rent, color:'var(--text-secondary)' },
          { label:'SALARIES', value:d.salaries, color:'var(--text-secondary)' },
          { label:'ROYALTY', value:d.royalty, color:'var(--navy)' },
          { label:'OTHER', value:d.otherExpenses, color:'var(--text-secondary)' },
          { label:'TOTAL EXP', value:exp, color:'var(--red)' },
          { label:'NET PROFIT', value:net, color:net>=0?'var(--green)':'var(--red)' },
          { label:'MARGIN', value:`${margin}%`, color:parseFloat(margin)>=40?'var(--green)':parseFloat(margin)>=20?'var(--amber)':'var(--red)' },
        ].map(item => (
          <div key={item.label} style={{ padding:'10px 12px', background:'var(--bg)', borderRadius:'var(--r-md)' }}>
            <div style={{ fontSize:9, color:'var(--text-tertiary)', letterSpacing:'0.08em', marginBottom:4 }}>{item.label}</div>
            <div style={{ fontSize:13, fontWeight:700, color:item.color, fontFamily:'DM Mono,monospace' }}>
              {typeof item.value === 'string' ? item.value : fmt(item.value)}
            </div>
          </div>
        ))}
      </div>

      {/* Edit request note */}
      {d.editRequestNote && (
        <div style={{ padding:'10px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', fontSize:12 }}>
          <strong>Edit request note:</strong> {d.editRequestNote}
        </div>
      )}

      {/* Approved edit request — show old vs new figures */}
      {d.editRequestApproved && !d.editRequested && !hasAmend && (d.amendedIncome != null || d.amendedRoyalty != null) && (
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', marginBottom:8 }}>✎ EDIT APPROVED — CHANGES MADE</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div style={{ padding:'12px 14px', background:'rgba(163,45,45,0.05)', borderRadius:'var(--r-md)', border:'1px solid rgba(163,45,45,0.15)' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--red)', marginBottom:8 }}>ORIGINAL</div>
              {[['Income',d.originalIncome??d.income],['Rent',d.originalRent??d.rent],['Salaries',d.originalSalaries??d.salaries],['Royalty',d.originalRoyalty??d.royalty],['Other',d.originalOtherExpenses??d.otherExpenses]].map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                  <span style={{ color:'var(--text-tertiary)' }}>{l}</span><span>{fmt(v)}</span>
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 14px', background:'var(--green-bg)', borderRadius:'var(--r-md)', border:'1px solid rgba(15,110,86,0.2)' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--green)', marginBottom:8 }}>APPROVED NEW FIGURES</div>
              {[['Income',d.amendedIncome,d.income],['Rent',d.amendedRent,d.rent],['Salaries',d.amendedSalaries,d.salaries],['Royalty',d.amendedRoyalty,d.royalty],['Other',d.amendedOtherExpenses,d.otherExpenses]].map(([l,av,ov]) => {
                const val = av!=null?av:ov;
                const changed = av!=null && String(av)!==String(ov);
                return (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                    <span style={{ color:'var(--text-tertiary)' }}>{l}</span>
                    <span style={{ color:changed?'var(--green)':'var(--text-secondary)', fontWeight:changed?700:400 }}>{fmt(val)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Amendment comparison */}
      {hasAmend && (
        <div>
          <div style={{ fontSize:11, fontWeight:700, color:'var(--text-tertiary)', marginBottom:8 }}>AMENDMENT</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
            <div style={{ padding:'12px 14px', background:'rgba(163,45,45,0.05)', borderRadius:'var(--r-md)', border:'1px solid rgba(163,45,45,0.15)' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--red)', marginBottom:8 }}>ORIGINAL</div>
              {[['Income',d.income],['Rent',d.rent],['Salaries',d.salaries],['Royalty',d.royalty],['Other',d.otherExpenses]].map(([l,v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                  <span style={{ color:'var(--text-tertiary)' }}>{l}</span><span>{fmt(v)}</span>
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 14px', background:'var(--blue-bg)', borderRadius:'var(--r-md)', border:'1px solid rgba(28,25,96,0.15)' }}>
              <div style={{ fontSize:10, fontWeight:700, color:'var(--blue)', marginBottom:8 }}>CORRECTED</div>
              {[['Income',d.amendedIncome,d.income],['Rent',d.amendedRent,d.rent],['Salaries',d.amendedSalaries,d.salaries],['Royalty',d.amendedRoyalty,d.royalty],['Other',d.amendedOtherExpenses,d.otherExpenses]].map(([l,av,ov]) => {
                const val = av!=null?av:ov;
                const changed = av!=null && String(av)!==String(ov);
                return (
                  <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                    <span style={{ color:'var(--text-tertiary)' }}>{l}</span>
                    <span style={{ color:changed?'var(--blue)':'var(--text-secondary)', fontWeight:changed?700:400 }}>{fmt(val)}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Royalty difference table */}
          {(() => {
            const origRoy = parseFloat(d.royalty)||0;
            const newRoy = d.amendedRoyalty!=null ? parseFloat(d.amendedRoyalty)||0 : origRoy;
            const diff = newRoy - origRoy;
            const origIncome = parseFloat(d.income)||0;
            const newIncome = d.amendedIncome!=null ? parseFloat(d.amendedIncome)||0 : origIncome;
            const origExp = (parseFloat(d.rent)||0)+(parseFloat(d.salaries)||0)+origRoy+(parseFloat(d.otherExpenses)||0);
            const newRent = d.amendedRent!=null?parseFloat(d.amendedRent):parseFloat(d.rent)||0;
            const newSal = d.amendedSalaries!=null?parseFloat(d.amendedSalaries):parseFloat(d.salaries)||0;
            const newOther = d.amendedOtherExpenses!=null?parseFloat(d.amendedOtherExpenses):parseFloat(d.otherExpenses)||0;
            const newExp = newRent+newSal+newRoy+newOther;
            const origNet = origIncome-origExp;
            const newNet = newIncome-newExp;
            return (
              <div style={{ marginTop:10 }}>
                <table className="table" style={{ fontSize:12 }}>
                  <thead><tr><th></th><th style={{ textAlign:'right' }}>ORIGINAL</th><th style={{ textAlign:'right' }}>CORRECTED</th><th style={{ textAlign:'right' }}>DIFFERENCE</th></tr></thead>
                  <tbody>
                    {[
                      ['Income', origIncome, newIncome],
                      ['Royalty', origRoy, newRoy],
                      ['Net Profit', origNet, newNet],
                    ].map(([label, orig, corr]) => {
                      const d2 = corr - orig;
                      return (
                        <tr key={label}>
                          <td style={{ fontWeight:500 }}>{label}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace' }}>{fmt(orig)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontWeight:600 }}>{fmt(corr)}</td>
                          <td style={{ textAlign:'right', fontFamily:'DM Mono,monospace', fontWeight:700, color:d2>0?'var(--red)':d2<0?'var(--green)':'var(--text-tertiary)' }}>
                            {d2>0?'+':''}{fmt(d2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {diff !== 0 && (
                  <div style={{ marginTop:8, padding:'10px 14px', borderRadius:'var(--r-md)', background:diff>0?'var(--amber-bg)':'var(--green-bg)', border:`1px solid ${diff>0?'rgba(217,119,6,0.3)':'rgba(15,110,86,0.2)'}`, fontSize:12, fontWeight:600 }}>
                    {diff > 0
                      ? `⚠ Additional royalty to collect: ${fmt(diff)}`
                      : `✓ Refund owed to franchisee: ${fmt(Math.abs(diff))}`
                    }
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Rejection reason */}
      {d.rejectionReason && (
        <div style={{ padding:'10px 14px', background:'var(--red-bg)', borderRadius:'var(--r-md)', fontSize:12, color:'var(--red)' }}>
          <strong>Rejection reason:</strong> {d.rejectionReason}
        </div>
      )}
    </div>
  );
}

// ─── Declaration Section (groups by year with collapsible) ─────────────────────
function ConfirmPayModal({ d, onConfirm, onCancel }) {
  const [method, setMethod] = React.useState('bank_transfer');
  const [datePaid, setDatePaid] = React.useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = React.useState(false);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'#fff', borderRadius:'var(--r-lg)', width:'100%', maxWidth:400, padding:28, boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ fontSize:15, fontWeight:700, color:'var(--navy)', marginBottom:4 }}>Confirm Payment</div>
        <div style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:20 }}>
          {d.franchiseName} &mdash; {d.month} &mdash; <strong>&pound;{parseFloat(d.royalty||0).toLocaleString()}</strong>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          <div>
            <label>PAYMENT METHOD</label>
            <select value={method} onChange={e=>setMethod(e.target.value)}>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cash">Cash</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
              <option value="jotform">JotForm</option>
              <option value="square">Square</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label>DATE PAID</label>
            <input type="date" value={datePaid} onChange={e=>setDatePaid(e.target.value)} />
          </div>
        </div>
        <div style={{ display:'flex', gap:8, marginTop:20 }}>
          <button className="btn btn-secondary" style={{ flex:1 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" style={{ flex:1 }} disabled={saving} onClick={async()=>{setSaving(true);await onConfirm(method,datePaid);setSaving(false);}}>
            {saving ? 'Saving...' : 'Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeclSection({ title, color, decls, isAdmin, onApprove, onReject, onEdit, onDelete, onDetail, onConfirmPayment, onApproveEdit, onRejectEdit, onSettleAmendment, groupByYear, collapsedYears, toggleYear, sectionKey }) {
  const grouped = groupByYear(decls);
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
        <div style={{ width:10, height:10, borderRadius:'50%', background:color, flexShrink:0 }} />
        <span style={{ fontWeight:700, fontSize:14, color:'var(--navy)' }}>{title}</span>
        <span style={{ fontSize:12, color:'var(--text-tertiary)' }}>({decls.length})</span>
      </div>
      {grouped.map(([year, yearDecls]) => {
        const key = `${sectionKey}_${year}`;
        const isCollapsed = collapsedYears[key];
        const sorted = yearDecls.sort((a,b) => parseMonthDate(b.month)-parseMonthDate(a.month));
        return (
          <div key={year} style={{ marginBottom:10 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:'rgba(28,25,96,0.04)', borderRadius:isCollapsed?'var(--r-md)':'var(--r-md) var(--r-md) 0 0', cursor:'pointer', border:'1px solid var(--border)', borderBottom:isCollapsed?'1px solid var(--border)':'none' }}
              onClick={() => toggleYear(sectionKey, year)}>
              <span style={{ fontSize:13, fontWeight:600, color:'var(--navy)' }}>{year}</span>
              <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>· {yearDecls.length} declaration{yearDecls.length>1?'s':''}</span>
              <i className={`ti ti-chevron-${isCollapsed?'down':'up'}`} style={{ fontSize:13, color:'var(--text-tertiary)', marginLeft:'auto' }} />
            </div>
            {!isCollapsed && (
              <div style={{ border:'1px solid var(--border)', borderTop:'none', borderRadius:'0 0 var(--r-md) var(--r-md)', overflow:'hidden' }}>
                {sorted.map(d => (
                  <DeclRow key={d.id} d={d} isAdmin={isAdmin} onApprove={onApprove} onReject={onReject} onEdit={onEdit} onDelete={onDelete} onDetail={onDetail} onConfirmPayment={onConfirmPayment} onApproveEdit={onApproveEdit} onRejectEdit={onRejectEdit} onSettleAmendment={onSettleAmendment} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Declaration Row (clickable) ───────────────────────────────────────────────
function DeclRow({ d, isAdmin, onApprove, onReject, onEdit, onDelete, onDetail, onConfirmPayment, onApproveEdit, onRejectEdit, onSettleAmendment }) {
  const exp = (parseFloat(d.rent)||0)+(parseFloat(d.salaries)||0)+(parseFloat(d.royalty)||0)+(parseFloat(d.otherExpenses)||0);
  const net = (parseFloat(d.income)||0)-exp;
  const hasAmend = d.amendedIncome !== null && d.amendedIncome !== undefined;

  return (
    <div style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderBottom:'1px solid var(--border)', cursor:'pointer', transition:'background 0.1s' }}
      onMouseEnter={e=>e.currentTarget.style.background='var(--bg)'}
      onMouseLeave={e=>e.currentTarget.style.background='transparent'}
      onClick={() => onDetail && onDetail(d)}>

      {/* Branch + month */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:2, flexWrap:'wrap' }}>
          {d.branchLocation ? (
            <>
              <span style={{ fontWeight:700, fontSize:13, color:'var(--navy)' }}>{d.branchLocation}</span>
              <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>({d.franchiseName})</span>
            </>
          ) : (
            <span style={{ fontWeight:700, fontSize:13, color:'var(--navy)' }}>{d.franchiseName}</span>
          )}
          <span style={{ fontWeight:600, fontSize:12, color:'var(--navy)' }}>· {d.month}</span>
          <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>· {d.franchiseeName}</span>
          {d.submittedAt && <span style={{ fontSize:11, color:'var(--text-tertiary)' }}>· {new Date(d.submittedAt).toLocaleDateString('en-GB')}</span>}
          {d.editRequestApproved && !d.editRequested && <Badge type="blue">✎ Edited</Badge>}
        </div>
        <div style={{ display:'flex', gap:10, fontSize:11, fontFamily:'DM Mono,monospace' }}>
          <span>Income: <strong style={{ color:'var(--navy)' }}>{fmt(d.income)}</strong></span>
          <span>Royalty: <strong style={{ color:'var(--navy)' }}>{fmt(d.royalty)}</strong></span>
          <span>Net: <strong style={{ color:net>=0?'var(--green)':'var(--red)' }}>{fmt(net)}</strong></span>
        </div>
        {d.editRequestNote && <div style={{ fontSize:11, color:'var(--blue)', marginTop:2 }}>Edit note: {d.editRequestNote}</div>}
        {hasAmend && !d.amendmentSettled && <div style={{ fontSize:11, color:'var(--amber)', marginTop:2 }}>⚠ Amendment pending</div>}
      </div>

      {/* Badges */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
        {d.editRequested ? <Badge type="blue">Edit Request</Badge> : <StatusBadge status={d.status} />}
        {d.paymentType === 'submit_and_pay' && <Badge type={d.paymentStatus==='confirmed'?'green':'amber'}>{d.paymentStatus==='confirmed'?'✓ Paid':'Pending'}</Badge>}
        {d.paymentType !== 'submit_and_pay' && d.paymentStatus === 'confirmed' && <Badge type="green">✓ Paid</Badge>}
        {hasAmend && !d.amendmentSettled && <Badge type="amber">Amendment</Badge>}
      </div>

      {/* Actions - stop propagation so clicking actions doesn't open detail */}
      {isAdmin && (
        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }} onClick={e=>e.stopPropagation()}>
          {d.status === 'pending' && !d.editRequested && <>
            <button className="btn btn-sm" style={{ background:'var(--green-bg)', color:'var(--green)', border:'1px solid rgba(15,110,86,0.2)', fontSize:11 }} onClick={() => onApprove && onApprove(d)}>✓</button>
            <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={() => onReject && onReject(d)}>✕</button>
          </>}
          {d.editRequested && <>
            <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => onApproveEdit && onApproveEdit(d)}>✓ Edit</button>
            <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={() => onRejectEdit && onRejectEdit(d)}>✕</button>
          </>}
          {d.paymentType === 'submit_and_pay' && d.paymentStatus !== 'confirmed' && (
            <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => onConfirmPayment && onConfirmPayment(d)}>Confirm Pay</button>
          )}
          {d.paymentType !== 'submit_and_pay' && d.status === 'approved' && d.paymentStatus !== 'confirmed' && (
            <button className="btn btn-sm" style={{ background:'var(--blue-badge-bg)', color:'var(--blue)', border:'none', fontSize:11 }} onClick={() => onConfirmPayment && onConfirmPayment(d)}>Mark Paid</button>
          )}
          {hasAmend && !d.amendmentSettled && d.status === 'approved' && (
            <button className="btn btn-sm" style={{ background:'var(--green-bg)', color:'var(--green)', border:'1px solid rgba(15,110,86,0.2)', fontSize:11 }} onClick={() => onSettleAmendment && onSettleAmendment(d)}>Settle</button>
          )}
          <button className="btn btn-sm btn-secondary" style={{ fontSize:11 }} onClick={() => onEdit && onEdit(d)}>Edit</button>
          <button className="btn btn-sm btn-danger" style={{ fontSize:11 }} onClick={() => onDelete && onDelete(d)}>Del</button>
        </div>
      )}

      <i className="ti ti-chevron-right" style={{ fontSize:13, color:'var(--text-tertiary)', flexShrink:0 }} />
    </div>
  );
}
